// Analytics Filters Panel Component
// Advanced filtering options for trade analysis
import React from 'react';
import { 
  BarChart3, 
  Clock, 
  Flame, 
  CheckCircle, 
  XCircle, 
  Target,
  Trophy,
  TrendingUp,
  TrendingDown,
  Calendar,
  PieChart,
  Cpu,
  User
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

// Filter label definitions - MUST match values from History.js getTradeProperties()
const CATEGORY_LABELS = {
  crypto: { fr: 'Crypto', en: 'Crypto' },
  stocks: { fr: 'Actions', en: 'Stocks' },
  commodities: { fr: 'Matières Premières', en: 'Commodities' },
  indices: { fr: 'Indices', en: 'Indices' },
  etf: { fr: 'ETF', en: 'ETF' },
};

const TRADE_TYPE_LABELS = {
  swing: { fr: 'Swing', en: 'Swing' },
  intraday: { fr: 'Intraday', en: 'Intraday' },
};

const RISK_PROFILE_LABELS = {
  prudent: { fr: 'Prudent', en: 'Prudent' },
  balanced: { fr: 'Équilibré', en: 'Balanced' },
  dynamic: { fr: 'Dynamique', en: 'Dynamic' },
};

const STATUS_LABELS = {
  win: { fr: 'Gagné', en: 'Won' },
  loss: { fr: 'Perdu', en: 'Lost' },
};

const ENTRY_TYPE_LABELS = {
  market: { fr: 'Au marché', en: 'Market' },
  optimal: { fr: 'Entrée Optimale', en: 'Optimal Entry' },
};

const TRADE_SOURCE_LABELS = {
  manual: { fr: 'Manuel', en: 'Manual' },
  autoscan: { fr: 'Auto-Scan', en: 'Auto-Scan' },
};

const LOSS_REASON_LABELS = {
  sl: { fr: 'SL Touché', en: 'SL Hit' },
  liquidation: { fr: 'Liquidé', en: 'Liquidated' },
};

const WIN_REASON_LABELS = {
  tp1: { fr: 'TP1 (25%)', en: 'TP1 (25%)' },
  tp2: { fr: 'TP2 (50%)', en: 'TP2 (50%)' },
  tp100: { fr: 'TP Final', en: 'TP Final' },
};

const DIRECTION_LABELS = {
  long: { fr: 'Long', en: 'Long' },
  short: { fr: 'Short', en: 'Short' },
};

// Helper component to render filter button group - defined outside to avoid re-renders
const FilterGroup = ({ 
  title, 
  icon: Icon, 
  filterKey, 
  labels, 
  colorConfig, 
  analyticsFilters, 
  toggleFilter, 
  language 
}) => (
  <div className="space-y-2">
    <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
      <Icon className="w-4 h-4" />
      {title}
    </div>
    <div className="flex flex-wrap gap-1">
      {Object.entries(labels).map(([key, labelObj]) => {
        const isActive = analyticsFilters[filterKey]?.includes(key);
        const { activeBg, inactiveBorder } = colorConfig(key, isActive);
        
        return (
          <Button
            key={key}
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter(filterKey, key)}
            className={`text-xs ${isActive ? activeBg : inactiveBorder}`}
            data-testid={`filter-${filterKey}-${key}`}
          >
            {labelObj[language] || labelObj.en}
          </Button>
        );
      })}
    </div>
  </div>
);

const AnalyticsFiltersPanel = ({
  language,
  analyticsFilters,
  toggleFilter,
  resetFilters,
  dateFilter,
  setDateFilter,
  variant = 'real' // 'real' or 'paper'
}) => {
  const isPaper = variant === 'paper';
  const borderColor = isPaper ? 'border-purple-500/30' : 'border-green-500/30';
  const bgGradient = isPaper 
    ? 'from-purple-900/20 to-purple-800/10' 
    : 'from-green-900/20 to-green-800/10';
  const iconColor = isPaper ? 'text-purple-400' : 'text-green-400';

  return (
    <Card className={`bg-gradient-to-r ${bgGradient} ${borderColor}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PieChart className={`w-5 h-5 ${iconColor}`} />
            {language === 'fr' 
              ? (isPaper ? 'Analyse Statistique Paper Trading' : 'Analyse Statistique Avancée')
              : (isPaper ? 'Paper Trading Analytics' : 'Advanced Statistical Analysis')}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="text-muted-foreground hover:text-white text-xs"
          >
            <XCircle className="w-4 h-4 mr-1" />
            {language === 'fr' ? 'Réinitialiser' : 'Reset'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          
          {/* Category */}
          <FilterGroup
            title={language === 'fr' ? 'Catégorie' : 'Category'}
            icon={BarChart3}
            filterKey="categories"
            labels={CATEGORY_LABELS}
            analyticsFilters={analyticsFilters}
            toggleFilter={toggleFilter}
            language={language}
            colorConfig={(key, isActive) => ({
              activeBg: 'bg-blue-500',
              inactiveBorder: 'border-blue-500/30 text-blue-400'
            })}
          />
          
          {/* Trade Type */}
          <FilterGroup
            title={language === 'fr' ? 'Type de Trade' : 'Trade Type'}
            icon={Clock}
            filterKey="tradeTypes"
            labels={TRADE_TYPE_LABELS}
            analyticsFilters={analyticsFilters}
            toggleFilter={toggleFilter}
            language={language}
            colorConfig={(key, isActive) => ({
              activeBg: 'bg-cyan-500',
              inactiveBorder: 'border-cyan-500/30 text-cyan-400'
            })}
          />
          
          {/* Risk Profile */}
          <FilterGroup
            title={language === 'fr' ? 'Profil de Risque' : 'Risk Profile'}
            icon={Flame}
            filterKey="riskProfiles"
            labels={RISK_PROFILE_LABELS}
            analyticsFilters={analyticsFilters}
            toggleFilter={toggleFilter}
            language={language}
            colorConfig={(key, isActive) => ({
              activeBg: key === 'prudent' ? 'bg-green-500' : key === 'balanced' ? 'bg-yellow-500' : 'bg-red-500',
              inactiveBorder: key === 'prudent' ? 'border-green-500/30 text-green-400' : key === 'balanced' ? 'border-yellow-500/30 text-yellow-400' : 'border-red-500/30 text-red-400'
            })}
          />
          
          {/* Result (Win/Loss) */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              {language === 'fr' ? 'Résultat' : 'Result'}
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(STATUS_LABELS).map(([key, labels]) => {
                const isActive = analyticsFilters.statuses?.includes(key);
                return (
                  <Button
                    key={key}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleFilter('statuses', key)}
                    className={`text-xs ${
                      isActive
                        ? key === 'win' ? 'bg-green-500' : 'bg-red-500'
                        : key === 'win' ? 'border-green-500/30 text-green-400' : 'border-red-500/30 text-red-400'
                    }`}
                  >
                    {key === 'win' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                    {labels[language] || labels.en}
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* Entry Type */}
          <FilterGroup
            title={language === 'fr' ? "Type d'Entrée" : 'Entry Type'}
            icon={Target}
            filterKey="entryTypes"
            labels={ENTRY_TYPE_LABELS}
            analyticsFilters={analyticsFilters}
            toggleFilter={toggleFilter}
            language={language}
            colorConfig={(key, isActive) => ({
              activeBg: 'bg-indigo-500',
              inactiveBorder: 'border-indigo-500/30 text-indigo-400'
            })}
          />
          
          {/* Trade Source - Manual vs Auto-Scan */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Cpu className="w-4 h-4" />
              {language === 'fr' ? 'Source' : 'Source'}
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(TRADE_SOURCE_LABELS).map(([key, labels]) => {
                const isActive = analyticsFilters.tradeSources?.includes(key);
                return (
                  <Button
                    key={key}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleFilter('tradeSources', key)}
                    className={`text-xs ${
                      isActive
                        ? key === 'manual' ? 'bg-blue-500' : 'bg-violet-500'
                        : key === 'manual' ? 'border-blue-500/30 text-blue-400' : 'border-violet-500/30 text-violet-400'
                    }`}
                    data-testid={`filter-tradeSources-${key}`}
                  >
                    {key === 'manual' ? <User className="w-3 h-3 mr-1" /> : <Cpu className="w-3 h-3 mr-1" />}
                    {labels[language] || labels.en}
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* Loss Reason */}
          <FilterGroup
            title={language === 'fr' ? 'Raison de Perte' : 'Loss Reason'}
            icon={XCircle}
            filterKey="lossReasons"
            labels={LOSS_REASON_LABELS}
            analyticsFilters={analyticsFilters}
            toggleFilter={toggleFilter}
            language={language}
            colorConfig={(key, isActive) => ({
              activeBg: 'bg-red-600',
              inactiveBorder: 'border-red-500/30 text-red-400'
            })}
          />
          
          {/* Exit Level (Win Reason) */}
          <FilterGroup
            title={language === 'fr' ? 'Niveau de Sortie' : 'Exit Level'}
            icon={Trophy}
            filterKey="winReasons"
            labels={WIN_REASON_LABELS}
            analyticsFilters={analyticsFilters}
            toggleFilter={toggleFilter}
            language={language}
            colorConfig={(key, isActive) => ({
              activeBg: 'bg-green-600',
              inactiveBorder: 'border-green-500/30 text-green-400'
            })}
          />
          
          {/* Direction */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              {language === 'fr' ? 'Direction' : 'Direction'}
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(DIRECTION_LABELS).map(([key, labels]) => {
                const isActive = analyticsFilters.directions?.includes(key);
                return (
                  <Button
                    key={key}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleFilter('directions', key)}
                    className={`text-xs ${
                      isActive
                        ? key === 'long' ? 'bg-green-500' : 'bg-red-500'
                        : key === 'long' ? 'border-green-500/30 text-green-400' : 'border-red-500/30 text-red-400'
                    }`}
                  >
                    {key === 'long' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                    {labels[language] || labels.en}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Date Filter */}
        <div className="flex flex-wrap items-center gap-4 p-3 rounded-lg bg-secondary/30 border border-white/10">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {language === 'fr' ? 'Période :' : 'Period:'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Start Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-[130px] justify-start text-left font-normal text-xs bg-background border-white/20"
                >
                  <Calendar className="mr-2 h-3 w-3" />
                  {dateFilter.startDate ? (
                    format(dateFilter.startDate, 'dd/MM/yyyy', { locale: language === 'fr' ? fr : enUS })
                  ) : (
                    <span className="text-muted-foreground">{language === 'fr' ? 'Du...' : 'From...'}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-background border-white/20" align="start">
                <CalendarComponent
                  mode="single"
                  selected={dateFilter.startDate}
                  onSelect={(date) => setDateFilter(prev => ({ ...prev, startDate: date }))}
                  locale={language === 'fr' ? fr : enUS}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <span className="text-muted-foreground">→</span>
            
            {/* End Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-[130px] justify-start text-left font-normal text-xs bg-background border-white/20"
                >
                  <Calendar className="mr-2 h-3 w-3" />
                  {dateFilter.endDate ? (
                    format(dateFilter.endDate, 'dd/MM/yyyy', { locale: language === 'fr' ? fr : enUS })
                  ) : (
                    <span className="text-muted-foreground">{language === 'fr' ? 'Au...' : 'To...'}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-background border-white/20" align="start">
                <CalendarComponent
                  mode="single"
                  selected={dateFilter.endDate}
                  onSelect={(date) => setDateFilter(prev => ({ ...prev, endDate: date }))}
                  locale={language === 'fr' ? fr : enUS}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          {(dateFilter.startDate || dateFilter.endDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDateFilter({ startDate: null, endDate: null })}
              className="text-muted-foreground hover:text-white text-xs"
            >
              <XCircle className="w-3 h-3 mr-1" />
              {language === 'fr' ? 'Effacer dates' : 'Clear dates'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsFiltersPanel;
